import { useEffect, useRef } from 'react'

import {
  AnalyticsConsent,
  REQUEST_CONSENT_MESSAGE,
  USERCENTRICS_CONSENT_CHANGED_MESSAGE,
  UcServiceSnapshot
} from './useUsercentricsConsent'

// Snapshots all UC services at call time so the iframe can sync its full UC
// SDK state, not just the 3 analytics-tracked services.
const readUcServices = (): UcServiceSnapshot[] | undefined => {
  const services = window.UC_UI?.getServicesBaseInfo?.()
  if (!services?.length) return undefined
  return services
    .filter((s): s is typeof s & { id: string } => !!s.id)
    .map(s => ({
      id: s.id,
      name: s.name ?? '(unknown)',
      consent:
        typeof s.consent === 'boolean' ? s.consent : s.consent?.status === true
    }))
}

const postToIframes = (consent: AnalyticsConsent) => {
  const ucServices = readUcServices()
  document
    .querySelectorAll<HTMLIFrameElement>('iframe[id^="iframe-"]')
    .forEach(iframe => {
      iframe.contentWindow?.postMessage(
        { type: USERCENTRICS_CONSENT_CHANGED_MESSAGE, consent, ucServices },
        window.location.origin
      )
    })
}

export const useBroadcastUsercentricsConsent = (consent?: AnalyticsConsent) => {
  const consentRef = useRef(consent)
  useEffect(() => {
    consentRef.current = consent
  }, [consent])

  // Broadcast when our 3 tracked analytics services change (covers initial
  // UC_UI_INITIALIZED sync when analyticsConsent first becomes non-default).
  useEffect(() => {
    if (!consent) return
    postToIframes(consent)
  }, [consent?.amplitude, consent?.googleAnalytics, consent?.clarity]) // eslint-disable-line react-hooks/exhaustive-deps

  // Broadcast on every UC consent event so changes to non-tracked services
  // (services outside amplitude / googleAnalytics / clarity) also sync to iframes.
  // Only registered in the top-level container — iframes are not authoritative
  // for consent and would loop when syncUcSdkState fires UC_UI_CMP_EVENT.
  useEffect(() => {
    if (window.parent !== window) return
    const onUcEvent = () => {
      if (!consentRef.current) return
      // Defer by one tick — UC_UI_CMP_EVENT can fire before the SDK commits
      // the new consent to getServicesBaseInfo() (e.g. when the banner opens).
      // setTimeout(0) ensures we read a settled snapshot.
      setTimeout(() => {
        if (consentRef.current) postToIframes(consentRef.current)
      }, 0)
    }
    window.addEventListener('UC_UI_CMP_EVENT', onUcEvent)
    return () => window.removeEventListener('UC_UI_CMP_EVENT', onUcEvent)
  }, [])

  // Respond to newly opened iframes requesting the current consent on mount.
  useEffect(() => {
    const handleConsentRequest = (e: MessageEvent) => {
      if (e.data?.type !== REQUEST_CONSENT_MESSAGE) return
      if (e.origin !== window.location.origin) return
      if (!consentRef.current) return
      const ucServices = readUcServices()
      ;(e.source as Window | null)?.postMessage(
        {
          type: USERCENTRICS_CONSENT_CHANGED_MESSAGE,
          consent: consentRef.current,
          ucServices
        },
        e.origin
      )
    }
    window.addEventListener('message', handleConsentRequest)
    return () => window.removeEventListener('message', handleConsentRequest)
  }, [])
}
